import { useEffect, useRef } from "react";

import { refreshAccess } from "@/api/session";
import { platformRole } from "@/app/registry";
import { useSession } from "@/app/session";
import { formatDate, formatINR } from "@/lib/format";
import {
  usePlatform,
  type Access,
  type Account,
} from "@/pages/account/platform";

/**
 * Pages a user whose plan isn't active can still open (2026-10-25: an expired
 * member signs in to these only; the API answers 402 `plan_inactive` to the
 * rest, see api/deps.py `PLAN_FREE_PREFIXES`). Help, the FAQ and the legal
 * pages are public pages outside the app, so they stay open too. A new Research
 * Analyst also opens SEBI registration (V064: no free trial on RA plans), so the
 * register code step can be finished before the first payment.
 */
export const UNGATED = ["/app/account", "/app/payment", "/app/onboarding"];

export function isUngated(path: string): boolean {
  return UNGATED.some((p) => path === p || path.startsWith(`${p}/`));
}

function useMe(): Account | undefined {
  const { user } = useSession();
  // admins and employees (V036 staff) have no plan
  const payer =
    !!user && !["admin", "employee"].includes(platformRole(user.role) ?? "");
  const me = usePlatform<Account>("/platform/me", undefined, payer);
  return payer ? me.data : undefined;
}

/** The signed-in investor's or RA's plan access (admins: undefined). */
export function useAccess(): Access | undefined {
  return useMe()?.access;
}

/** What they pay a month once the current period ends (V065 complimentary periods). */
export function useNextPrice(): number | null {
  return useMe()?.subscription?.next_price ?? null;
}

/** "Complimentary until 25 Nov 2026, then ₹2,999/month incl. GST" (V065). */
export function complimentaryText(until: string | null | undefined, next: number | null): string {
  const end = until ? ` until ${formatDate(until)}` : "";
  return `Complimentary${end}${next ? `, then ${formatINR(next, 0)}/month incl. GST` : ""}`;
}

/** A complimentary period shows a pay link only in its last 7 days. */
export function payLinkShown(a: Access): boolean {
  if (a.kind !== "complimentary") return true;
  return a.days_left != null && a.days_left <= 7;
}

/**
 * The plan has ended (or was never activated): the session says so at sign-in
 * (`plan_active`), and /platform/me says so live. Admins and employees never.
 * When /platform/me reports the plan active again (an admin approved the
 * payment) while the session's token still says inactive, the session is
 * refreshed once, so the API opens up without signing in again.
 */
export function usePlanInactive(): boolean {
  const { user } = useSession();
  const access = useAccess();
  const tokenInactive = user?.plan_active === false;
  const refreshed = useRef(false);
  useEffect(() => {
    if (tokenInactive && access?.active && !refreshed.current) {
      refreshed.current = true;
      void refreshAccess();
    }
  }, [tokenInactive, access?.active]);
  if (access) return !access.active;
  return tokenInactive;
}

/** "Your free trial has ended" / "Your membership has ended" (the screen's heading). */
export function endedTitle(reason: string | undefined): string {
  if (reason === "trial_ended") return "Your free trial has ended";
  if (reason === "expired") return "Your membership has ended";
  return "Your membership isn't active yet";
}

/** The plan's state in one line, from the stored period (days come from the API). */
export function bannerText(a: Access, nextPrice: number | null = null): string {
  const until = a.until ? formatDate(a.until) : null;
  const days = a.days_left ?? null;
  const left =
    days === null
      ? ""
      : days <= 0
        ? "ends today"
        : `${days} day${days === 1 ? "" : "s"} left`;
  if (a.kind === "ra_client") {
    const ra = a.ra_name ?? "your analyst";
    if (a.reason === "ra_client_grace") {
      const on = a.grace_until ? formatDate(a.grace_until) : null;
      return `Your analyst's service has ended. Become a member within 7 days to keep your account${on ? ` (inactive on ${on})` : ""}`;
    }
    if (a.reason === "ra_client_ended")
      return "Your analyst's service has ended";
    return `Client of ${ra}: your analyst's plan covers this account`;
  }
  if (!a.active) {
    if (a.reason === "expired")
      return `Your membership ended${until ? ` on ${until}` : ""}`;
    if (a.reason === "trial_ended")
      return `Your trial ended${until ? ` on ${until}` : ""}`;
    return "Your account is waiting for payment";
  }
  if (a.kind === "trial")
    return `Free trial: ${left}${until ? ` (last day ${until})` : ""}`;
  if (a.kind === "complimentary")
    return `${a.plan_name ?? "Your plan"}: ${complimentaryText(a.until, nextPrice)}`;
  const plan = a.plan_name ?? "Your plan";
  if (!until) return `${plan}: active`;
  return `${plan}: ${left} (renews manually on ${until})`;
}

export function bannerTone(a: Access): "ok" | "warn" | "down" {
  if (!a.active) return "down";
  if (a.reason === "ra_client_grace") return "down";
  if (a.reason === "ra_client") return "ok";
  const d = a.days_left;
  if (d === null || d === undefined) return "ok";
  // A trial is short anyway: blue while it runs, amber in the last 2 days, red on the last.
  if (a.kind === "trial") return d <= 0 ? "down" : d <= 2 ? "warn" : "ok";
  if (d <= 3) return "down";
  if (d <= 7) return "warn";
  return "ok";
}
